import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { toast } from 'sonner';
import {
  Percent, FileSpreadsheet, Landmark, Users, Plus, X, Download,
  AlertTriangle, ShieldAlert, BadgeCheck, Search,
} from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const inp = 'w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]';
const lbl = 'block text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500 mb-1';
const TH = 'px-3 py-2 text-left text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';
const THR = 'px-3 py-2 text-right text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';

const fyNow = (() => { const t = new Date(); const sy = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1; return `${sy}-${String(sy + 1).slice(2)}`; })();
const fyOptions = (() => { const sy = Number(fyNow.split('-')[0]); return [sy - 1, sy, sy + 1].map((y) => `${y}-${String(y + 1).slice(2)}`); })();

function exportCSV(filename, columns, rows) {
  const esc = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [columns.map((c) => esc(c.label)).join(',')]
    .concat(rows.map((r) => columns.map((c) => esc(typeof c.value === 'function' ? c.value(r) : r[c.key])).join(','))).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename; a.click(); URL.revokeObjectURL(a.href);
}

// ─── Summary matrix tab ──────────────────────────────────────────────────────
function SummaryTab({ fy }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); apiGet('/finance/tds/summary', { fy }).then(setD).catch(() => toast.error('Failed to load TDS summary')); }, [fy]);
  if (!d) return <div className="text-slate-400 text-sm py-6">Loading…</div>;
  const monthLabel = (m) => new Date(`${m}-01`).toLocaleString('en', { month: 'short', timeZone: 'Asia/Kolkata' });
  return (
    <div data-testid="tds-summary-tab">
      <div className="flex gap-3 mb-3">
        <div className="border rounded-sm px-4 py-2.5 border-slate-200"><div className="text-[10px] font-bold uppercase text-slate-500">Deducted FY</div><div className="font-display text-lg font-extrabold text-[#3b4872]" data-testid="tds-deducted-total">{formatINR(d.totals?.deducted)}</div></div>
        <div className="border rounded-sm px-4 py-2.5 border-slate-200"><div className="text-[10px] font-bold uppercase text-slate-500">Deposited FY</div><div className="font-display text-lg font-extrabold text-emerald-700" data-testid="tds-deposited-total">{formatINR(d.totals?.deposited)}</div></div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr><th className={TH}>Section</th>{d.months.map((m) => <th key={m} className={THR}>{monthLabel(m)}</th>)}<th className={THR}>Deducted</th><th className={THR}>Deposited</th><th className={THR}>Variance</th></tr>
          </thead>
          <tbody>
            {d.rows.length === 0 && <tr><td colSpan={16} className="text-center py-8 text-slate-500" data-testid="tds-summary-empty">No TDS deducted in FY {fy} yet. TDS is computed on vendor invoices and reconciled at payment approval.</td></tr>}
            {d.rows.map((r) => (
              <tr key={r.section} className="border-t border-slate-100" data-testid={`tds-summary-row-${r.section}`}>
                <td className="px-3 py-2 font-bold">{r.section}
                  {r.late_deposit_risk && <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200 rounded-sm" title={`Deducted but not deposited for: ${r.late_months.join(', ')} — interest u/s 201(1A) risk`} data-testid={`tds-late-risk-${r.section}`}><AlertTriangle size={9} /> late</span>}
                </td>
                {d.months.map((m) => (
                  <td key={m} className="px-2 py-2 text-right">
                    {r.months[m]?.deducted ? <div className="font-bold">{formatINR(r.months[m].deducted)}</div> : <span className="text-slate-300">—</span>}
                    {r.months[m]?.deposited ? <div className="text-emerald-700">{formatINR(r.months[m].deposited)}</div> : null}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-bold">{formatINR(r.deducted_total)}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{formatINR(r.deposited_total)}</td>
                <td className={`px-3 py-2 text-right font-bold ${r.variance > 0 ? 'text-rose-600' : 'text-slate-500'}`}>{formatINR(r.variance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">Top figure = deducted (by payment approval month) · green = deposited via challans. Variance &gt; 0 on a past month flags late-deposit interest risk (Sec 201(1A)).</div>
    </div>
  );
}

// ─── 26Q tab ─────────────────────────────────────────────────────────────────
function Q26Tab({ fy }) {
  const [quarter, setQuarter] = useState('Q1');
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); apiGet('/finance/tds/26q-data', { fy, quarter }).then(setD).catch(() => toast.error('Failed to load 26Q data')); }, [fy, quarter]);
  const cols = [
    { key: 'vendor_name', label: 'Deductee' }, { key: 'pan', label: 'PAN' }, { key: 'section', label: 'Section' },
    { key: 'paid_total', label: 'Amount Paid' }, { key: 'tds_total', label: 'TDS Deducted' },
    { key: 'challans', label: 'Challan Refs', value: (r) => (r.challans || []).map((c) => c.challan_no).join(' | ') },
  ];
  return (
    <div data-testid="tds-26q-tab">
      <div className="flex items-center gap-2 mb-3">
        {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
          <button key={q} onClick={() => setQuarter(q)} className={`px-3 py-1.5 text-xs font-display font-bold rounded-sm border ${quarter === q ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600'}`} data-testid={`q26-quarter-${q}`}>{q}</button>
        ))}
        {d && <span className="text-xs text-slate-500 ml-2">{d.window.frm} → {d.window.to} · {d.summary.deductees} deductees · TDS {formatINR(d.summary.tds_total)}</span>}
        <button className="ml-auto flex items-center gap-1 text-xs font-bold text-[#3b4872] hover:underline" onClick={() => d && exportCSV(`26Q-${fy}-${quarter}.csv`, cols, d.rows)} data-testid="q26-export"><Download size={12} /> CSV for CA</button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className={TH}>Deductee</th><th className={TH}>PAN</th><th className={TH}>Section</th><th className={THR}>Paid</th><th className={THR}>TDS</th><th className={TH}>Challans</th></tr></thead>
          <tbody>
            {!d && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {d && d.rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500" data-testid="q26-empty">No TDS payments approved in {quarter} of FY {fy}. The quarter is the quarter of PAYMENT (voucher approval), not the invoice date.</td></tr>}
            {(d?.rows || []).map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`q26-row-${r.vendor_name}-${r.section}`}>
                <td className="px-3 py-2 font-bold">{r.vendor_name}</td>
                <td className="px-3 py-2 text-xs">{r.pan || <span className="text-rose-600 font-bold">NO PAN</span>}</td>
                <td className="px-3 py-2 text-xs font-bold">{r.section}</td>
                <td className="px-3 py-2 text-right font-display">{formatINR(r.paid_total)}</td>
                <td className="px-3 py-2 text-right font-display font-bold">{formatINR(r.tds_total)}</td>
                <td className="px-3 py-2 text-xs">{(r.challans || []).map((c) => c.challan_no).join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">Vendor-side 194-series only — salary TDS (24Q) lives in Payroll. The CA imports this CSV into the 26Q utility; FVU generation is out of scope.</div>
    </div>
  );
}

// ─── Challans tab ────────────────────────────────────────────────────────────
function ChallansTab({ fy }) {
  const [d, setD] = useState(null);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ challan_no: '', bsr_code: '', deposit_date: '', month: '', section: '194C', amount: '' });
  const load = useCallback(() => apiGet('/finance/tds/challans', { fy }).then(setD).catch(() => {}), [fy]);
  useEffect(() => { load(); }, [load]);
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.challan_no || !f.deposit_date || !f.month || Number(f.amount) <= 0) return toast.error('Challan no, deposit date, month and amount are required');
    try {
      await apiPost('/finance/tds/challans', { ...f, amount: Number(f.amount) });
      toast.success('Challan recorded'); setShow(false); setF({ challan_no: '', bsr_code: '', deposit_date: '', month: '', section: '194C', amount: '' }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div data-testid="tds-challans-tab">
      <div className="flex justify-between mb-3">
        <div className="text-xs text-slate-500">{d ? `${d.count} challans · ${formatINR(d.total)} deposited in FY ${fy}` : ''}</div>
        <button onClick={() => setShow(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase text-white bg-[#3b4872] rounded-sm" data-testid="challan-add-btn"><Plus size={13} /> Add Challan</button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className={TH}>Challan No</th><th className={TH}>BSR</th><th className={TH}>Deposit Date</th><th className={TH}>For Month</th><th className={TH}>Section</th><th className={THR}>Amount</th></tr></thead>
          <tbody>
            {(d?.items || []).length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500" data-testid="challan-empty">No challans recorded for FY {fy}.</td></tr>}
            {(d?.items || []).map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`challan-row-${c.challan_no}`}>
                <td className="px-3 py-2 font-bold">{c.challan_no}</td>
                <td className="px-3 py-2 text-xs">{c.bsr_code || '—'}</td>
                <td className="px-3 py-2 text-xs">{formatDate(c.deposit_date)}</td>
                <td className="px-3 py-2 text-xs">{c.month}</td>
                <td className="px-3 py-2 text-xs font-bold">{c.section}</td>
                <td className="px-3 py-2 text-right font-display font-bold">{formatINR(c.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid="challan-drawer">
          <div className="bg-white h-full w-[440px] max-w-full p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-lg font-extrabold">Record TDS Challan</h2><button onClick={() => setShow(false)} className="text-slate-400"><X size={18} /></button></div>
            <div className="space-y-3">
              <div><label className={lbl}>Challan No (CIN)</label><input className={inp} value={f.challan_no} onChange={(e) => u('challan_no', e.target.value)} data-testid="challan-form-no" /></div>
              <div><label className={lbl}>BSR Code</label><input className={inp} value={f.bsr_code} onChange={(e) => u('bsr_code', e.target.value)} data-testid="challan-form-bsr" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Deposit Date</label><input type="date" className={inp} value={f.deposit_date} onChange={(e) => u('deposit_date', e.target.value)} data-testid="challan-form-date" /></div>
                <div><label className={lbl}>For Month</label><input type="month" className={inp} value={f.month} onChange={(e) => u('month', e.target.value)} data-testid="challan-form-month" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Section</label><select className={inp} value={f.section} onChange={(e) => u('section', e.target.value)} data-testid="challan-form-section">{['194C', '194J', '194I', '194Q'].map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><label className={lbl}>Amount (₹)</label><input type="number" className={inp} value={f.amount} onChange={(e) => u('amount', e.target.value)} data-testid="challan-form-amount" /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm" onClick={() => setShow(false)}>Cancel</button>
              <button className="px-4 py-2 text-xs font-bold uppercase text-white bg-[#3b4872] rounded-sm" onClick={save} data-testid="challan-form-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Vendor TDS setup tab ────────────────────────────────────────────────────
function VendorsTab({ fy }) {
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => apiGet('/finance/tds/vendors', { fy, ...(q ? { q } : {}) }).then(setD).catch(() => {}), [fy, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div data-testid="tds-vendors-tab">
      <div className="relative mb-3 max-w-sm">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className={`${inp} pl-8`} placeholder="Search vendor / PAN…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tds-vendor-search" />
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className={TH}>Vendor</th><th className={TH}>PAN</th><th className={TH}>Type</th><th className={TH}>Section</th><th className={TH}>Flags</th><th className={THR}>FY Paid</th><th className={THR}>FY TDS</th><th className={TH}></th></tr></thead>
          <tbody>
            {(d?.items || []).map((v) => (
              <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tds-vendor-row-${v.vendor_code || v.id}`}>
                <td className="px-3 py-2 font-bold">{v.name}<span className="text-[10px] text-slate-400 ml-1">{v.vendor_code}</span></td>
                <td className="px-3 py-2 text-xs">{v.pan || <span className="text-rose-600 font-bold" title="206AA — 20% flat without PAN">NO PAN → 20%</span>}</td>
                <td className="px-3 py-2 text-xs">{v.pan_type || 'company'}</td>
                <td className="px-3 py-2 text-xs font-bold">{v.tds_section || '—'}</td>
                <td className="px-3 py-2">
                  {v.tds_206ab_flagged && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200 rounded-sm mr-1" data-testid={`flag-206ab-${v.vendor_code}`}><ShieldAlert size={9} /> 206AB</span>}
                  {v.lower_deduction_cert && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm" title={`${v.lower_deduction_cert.cert_no} @ ${v.lower_deduction_cert.rate}% till ${v.lower_deduction_cert.valid_to}`} data-testid={`flag-ldc-${v.vendor_code}`}><BadgeCheck size={9} /> LDC {v.lower_deduction_cert.rate}%</span>}
                </td>
                <td className="px-3 py-2 text-right font-display">{formatINR(v.fy_paid)}</td>
                <td className="px-3 py-2 text-right font-display font-bold">{formatINR(v.fy_tds_deducted)}</td>
                <td className="px-3 py-2"><button onClick={() => setEditing(v)} className="text-[11px] font-bold text-[#3b4872] hover:underline" data-testid={`tds-vendor-edit-${v.vendor_code}`}>TDS Setup</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <VendorTDSDrawer vendor={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function VendorTDSDrawer({ vendor, onClose, onSaved }) {
  const [f, setF] = useState({
    pan_type: vendor.pan_type || 'company', tds_section: vendor.tds_section || '194C',
    tds_206ab_flagged: !!vendor.tds_206ab_flagged,
    ldc_enabled: !!vendor.lower_deduction_cert,
    cert_no: vendor.lower_deduction_cert?.cert_no || '', rate: vendor.lower_deduction_cert?.rate || '',
    valid_from: vendor.lower_deduction_cert?.valid_from || '', valid_to: vendor.lower_deduction_cert?.valid_to || '',
    limit: vendor.lower_deduction_cert?.limit || '',
  });
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = {
      pan_type: f.pan_type, tds_section: f.tds_section, tds_206ab_flagged: f.tds_206ab_flagged,
      ...(f.ldc_enabled
        ? { lower_deduction_cert: { cert_no: f.cert_no, rate: Number(f.rate), valid_from: f.valid_from, valid_to: f.valid_to, limit: Number(f.limit || 0) } }
        : { clear_ldc: true }),
    };
    try {
      await apiPut(`/finance/tds/vendor-config/${vendor.id}`, body);
      toast.success('Vendor TDS setup saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid="vendor-tds-drawer">
      <div className="bg-white h-full w-[460px] max-w-full overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-1"><h2 className="font-display text-lg font-extrabold">TDS Setup — {vendor.name}</h2><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        <div className="text-xs text-slate-500 mb-4">PAN: {vendor.pan || 'MISSING (20% flat u/s 206AA)'}</div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>PAN Type</label><select className={inp} value={f.pan_type} onChange={(e) => u('pan_type', e.target.value)} data-testid="vendor-tds-pantype"><option value="individual">Individual / HUF</option><option value="company">Company</option><option value="firm">Firm</option></select></div>
            <div><label className={lbl}>Default Section</label><select className={inp} value={f.tds_section} onChange={(e) => u('tds_section', e.target.value)} data-testid="vendor-tds-section">{['194C', '194J', '194I', '194Q'].map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.tds_206ab_flagged} onChange={(e) => u('tds_206ab_flagged', e.target.checked)} data-testid="vendor-tds-206ab" /> 206AB non-filer (CA-confirmed) — higher of 2× rate or 5%</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.ldc_enabled} onChange={(e) => u('ldc_enabled', e.target.checked)} data-testid="vendor-tds-ldc-enable" /> Lower Deduction Certificate (197)</label>
          {f.ldc_enabled && (
            <div className="border border-slate-200 rounded-sm p-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Certificate No</label><input className={inp} value={f.cert_no} onChange={(e) => u('cert_no', e.target.value)} data-testid="ldc-cert-no" /></div>
                <div><label className={lbl}>LDC Rate %</label><input type="number" className={inp} value={f.rate} onChange={(e) => u('rate', e.target.value)} data-testid="ldc-rate" /></div>
                <div><label className={lbl}>Valid From</label><input type="date" className={inp} value={f.valid_from} onChange={(e) => u('valid_from', e.target.value)} data-testid="ldc-from" /></div>
                <div><label className={lbl}>Valid To</label><input type="date" className={inp} value={f.valid_to} onChange={(e) => u('valid_to', e.target.value)} data-testid="ldc-to" /></div>
              </div>
              <div><label className={lbl}>Sanctioned Limit (₹ — total deduction cap)</label><input type="number" className={inp} value={f.limit} onChange={(e) => u('limit', e.target.value)} data-testid="ldc-limit" /></div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 text-xs font-bold uppercase text-white bg-[#3b4872] rounded-sm" onClick={save} data-testid="vendor-tds-save">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function TDSCenter() {
  const [tab, setTab] = useUrlTab('tab', 'summary');
  const [fy, setFy] = useState(fyNow);
  const TABS = [
    { id: 'summary', label: 'Summary Matrix', icon: Percent },
    { id: '26q', label: '26Q Export', icon: FileSpreadsheet },
    { id: 'challans', label: 'Challan Register', icon: Landmark },
    { id: 'vendors', label: 'Vendor TDS Setup', icon: Users },
  ];
  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="tds-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><Percent size={26} className="text-[#3b4872]" /> TDS Centre</h1>
          <p className="text-sm text-slate-600 mt-1">Vendor-side 194-series engine · thresholds, LDC, 206AA/AB · challans · 26Q prep. Salary TDS (24Q) stays in Payroll.</p>
        </div>
        <select className="border border-slate-300 rounded-sm px-3 py-2 text-sm" value={fy} onChange={(e) => setFy(e.target.value)} data-testid="tds-fy-picker">
          {fyOptions.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
      </div>
      <div className="flex border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px whitespace-nowrap ${tab === t.id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'summary' && <SummaryTab fy={fy} />}
      {tab === '26q' && <Q26Tab fy={fy} />}
      {tab === 'challans' && <ChallansTab fy={fy} />}
      {tab === 'vendors' && <VendorsTab fy={fy} />}
    </div>
  );
}
